/**
 * URL-driven board-comment panel state, so a comment can be linked and shared.
 *
 * The open row + focused comment live in the query string:
 *   `?item=<rowKey>`               → the panel opens for that row
 *   `?item=<rowKey>&comment=<id>`  → …and scrolls to / highlights that comment
 *
 * Keeping this in the URL (rather than component state) is what makes a comment
 * deeplink work: paste the link, the board mounts, the panel opens on the right
 * row, and the target comment is brought into view.
 */
import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

export function useCommentDeeplink(): {
  openRowKey: string | null;
  focusCommentId: string | null;
  openComments: (rowKey: string) => void;
  closeComments: () => void;
} {
  const [params, setParams] = useSearchParams();
  const openRowKey = params.get('item');
  const focusCommentId = params.get('comment');

  const openComments = useCallback(
    (rowKey: string) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          /* eslint-disable i18next/no-literal-string -- URL query parameter names */
          next.set('item', rowKey);
          next.delete('comment');
          /* eslint-enable i18next/no-literal-string */
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const closeComments = useCallback(() => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        /* eslint-disable i18next/no-literal-string -- URL query parameter names */
        next.delete('item');
        next.delete('comment');
        /* eslint-enable i18next/no-literal-string */
        return next;
      },
      { replace: true },
    );
  }, [setParams]);

  return { openRowKey, focusCommentId, openComments, closeComments };
}
